/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import _ from "lodash";
import { toHeaderCase } from "js-convert-case";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import config from "../config/boxDetail.config.json";
import samconfig from "../config/box.config.json";

import { useBoxId } from "../provider";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_BOX_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { AppNavigatorService } from "../../appNavigatorService";
import { capitalCase } from "change-case";
import moment from "moment-timezone";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { pretty } from "@vlinder-web/utils-react";

const log: any = LoggerModule?.getInstance();
const storageService = StorageModule.getInstance();

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IBoxDetailMethods extends IPluginMethodMap {
  getBoxDetail(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export class BoxDetailService implements IAgentPlugin {
  readonly methods: IBoxDetailMethods;

  constructor() {
    this.methods = {
      getBoxDetail: this.getBoxDetail.bind(this),
    };
  }

  async getBoxDetail(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { boxId } = useBoxId();
    console.log("args?.criteriaargs?.criteria", pretty(args?.criteria));
    try {
      if (client) {
        const res = await client.query({
          query: GET_BOX_DETAIL,
          variables: {
            filters: {
              ...args?.criteria,
              _id__in: boxId,
            },
          },
          fetchPolicy: "network-only",
        });
        // const res: any = samconfig;
        log.success(`get box data :`, pretty(res));

        if (res?.data?.getAdptBoxes) {
          // log.success(`get box data in boxDetail screen :`, res?.data?.getAdptBoxes);
          return {
            isEmpty: res?.data?.getAdptBoxes ? false : true,
            title: this.formatTitle(res?.data?.getAdptBoxes),
            subtitle: this.formatSubTitle(res?.data?.getAdptBoxes),
            listItems: this.formatAttributeItems(
              res?.data?.getAdptBoxes?.content[0]
            ),
            initialData: {
              boxBand:
                storageService.get("currencyExchangeLocal", "local")?.band ??
                null,
              isVIP: res?.data?.getAdptBoxes?.content[0]?.isVIP,
              deliveryOptions: this.formatDeliveryOptions(
                res?.data?.enum_shippingProviders,
                res?.data?.getAdptBoxes?.shippingDetails
              ),
              isOpted: res?.data?.getAdptBoxes?.shippingDetails?.isOpted,
            },
            // buttons: [
            //   {
            //     variant: "primary",
            //     title: "Upgrade to VIP",
            //     key: "updateBox",
            //     isPresent: true,
            //   },
            //   {
            //     variant: "primary",
            //     title: "Opt for mail delivery",
            //     key: "optMailDelivery",
            //     isPresent: true,
            //   },
            // ],
            // boxTransactions: this.formatBoxTransactions(
            //   res?.data?.getAdptBoxes?.boxTxes
            // ),
          };
        }
      }
    } catch (err: any) {
      throw err;
    }
  }

  private formatDeliveryOptions(deliveryOptions: any, data: any) {
    let _res: any[] = [];
    let _firsthalf: any[] = [];
    deliveryOptions &&
      Array.isArray(deliveryOptions) &&
      deliveryOptions.length > 0 &&
      deliveryOptions.forEach((option: any) => {
        if (data?.isOpted && data?.provider === option) {
          _firsthalf.push({
            label: option,
            value: option,
          });
        } else {
          _res.push({
            label: option,
            value: option,
          });
        }
      });

    return [..._firsthalf, ..._res];
  }

  // private capitalizeEachWord(str: any) {
  //   let words = str.split(' ');
  //   for (let i = 0; i < words.length; i++) {
  //     words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);

  //   }
  //   return words.join(' ');
  // }

  // private formatBoxTransactions(boxTxns: any) {
  //   const eventTimeZone = storageService.get("currencyExchangeLocal", "local")?.eventTimeZone;
  //   let _res: any[] = [];
  //   console.log("box?.boxStatus=>", eventTimeZone)
  //   boxTxns &&
  //     Array.isArray(boxTxns) &&
  //     boxTxns.length > 0 &&
  //     boxTxns.forEach((txn: any) => {
  //       let _date =
  //         txn?.createdAt &&
  //         `${moment.tz(txn?.createdAt, eventTimeZone?.tmz).format("LLL")} ${eventTimeZone?.abbrevation
  //         }`;
  //       console.log("box?.boxStatus=>", _date)
  //       _res.push({
  //         title: _date,
  //         cardTitle: capitalCase(txn?.action),
  //         cardSubtitle: `By: ${txn?.user?.fullName ?
  //           this.capitalizeEachWord(txn?.user?.fullName) :
  //           txn?.user?.firstName ? this.capitalizeEachWord(txn?.user?.firstName) :
  //             (txn?.user?.email ? txn?.user?.email : "Null Null")}`,
  //       });
  //     });
  //   console.log("box?.boxStatus=>", _res)

  //   return _res;
  // }

  private formatTitle(box: any) {
    let formatted = box?.content[0]?.user?.fullName
      ? box?.content[0]?.user?.fullName
      : "";
    return toHeaderCase(formatted);
  }

  private formatSubTitle(box: any) {
    let formatted = box?.content[0]?.user?.email
      ? box?.content[0]?.user?.email
      : "";
    return formatted;
  }

  private formatAttributeItems(box: any) {
    const formatted: any = {
      info: config?.attributeItems,
    };
    console.log("box?.shippingDetails?.isOpted formatted", pretty(box));
    if (box) {
      if (box?.shippingDetails) {
        console.log("box?.shippingDetails?.isOpted", box?.shippingDetails);
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "shippingDetails"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.shippingDetails?.provider ?? "Distribution Center";
        }
      }
      if (box?.masqID) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "masqID"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.masqID;
        }
      }
      if (box?.costumeID) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "costumeID"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.costumeID;
        }
      }
      if (box?.isVIP.toString()) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "isVIP"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.isVIP
            ? box?.isVIP === true
              ? "Yes"
              : "No"
            : "No";
        }
      }
      if (box?.gender) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "gender"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.gender;
        }
      }
      if (box?.contentStatus) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "contentStatus"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.contentStatus;
        }
      }
      if (box?.paymentStatus) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "paymentStatus"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.paymentStatus && capitalCase(box?.paymentStatus.toString());
        }
      }
      if (box?.section) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "section"
        );
        if (index > -1) {
          formatted.info[index].subTitle =
            box?.section && capitalCase(box?.section.toString());
        }
      }
      if (box?.sectionOptType) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "sectionOptType"
        );
        if (index > -1) {
          if (box?.sectionOptType) {
            formatted.info[index].subTitle = box?.sectionOptType;
          }
        }
      }
      if (box?.deliveryStatus) {
        const index = _.findIndex(
          formatted.info,
          (info: any) => info.key === "deliveryStatus"
        );
        if (index > -1) {
          formatted.info[index].subTitle = box?.deliveryStatus ?? "Null";
        }
      }
    }
    return formatted?.info;
  }
}
